import { Box, Typography, Stack } from '@mui/material';
import { format } from 'date-fns';
import React from 'react';

import {
  LAND_RIGHT_TYPE_OPTIONS,
  PROPERTY_BUILDING_PERMIT_REASON_TYPE_VALUES,
} from '@app/static/constants';
import { theme } from '@app/theme';
import {
  Product,
  ProductCompletionDateType,
  ProductCompletionSeason,
  ProductDeliveryDateType,
  ProductDeliverySeason,
  ProductLandAreaType,
  ProductLandRightType,
  ProductRangeType,
  ProductReformExterior,
  ProductReformInterior,
  ProductSalesStatus,
  TransportMeans,
} from '@app/types/catalog';
import { parseDateStringToDateObj } from '@app/utils/date';
import {
  convertToTsubo,
  formatAreaRange,
  formatFloorPlan,
  formatPrice,
  formatPriceRange,
  getTransportMeanLabel,
  getTransportTimeLabel,
} from '@app/utils/format';

interface PropertyInfoProps {
  product: Product;
}

export function PropertyInfo({ product }: PropertyInfoProps) {
  const getDeliverySeasonLabel = (seasonKey?: string) => {
    if (!seasonKey) return '';
    const season = Object.values(ProductDeliverySeason).find(
      (season) => season.key === seasonKey
    );
    return season ? `${season.label}` : '';
  };
  const times = [
    product.customFields.transportMainMean === TransportMeans.WALK
      ? product.customFields.transportMainTimeMin
      : null,
    product.customFields.transportSub1Mean === TransportMeans.WALK
      ? product.customFields.transportSub1TimeMin
      : null,
    product.customFields.transportSub2Mean === TransportMeans.WALK
      ? product.customFields.transportSub2TimeMin
      : null,
  ].filter(
    (time): time is number => time !== null && time !== undefined && time > 0
  );
  const minTime = times.length > 0 ? Math.min(...times) : null;
  const rows = [
    {
      label: '販売状態',
      value: Object.values(ProductSalesStatus).find(
        (status) => status.key === product.customFields.salesStatus
      )?.label,
    },
    {
      label: '所在地',
      value:
        product.addressLine1 +
        product.addressLine2 +
        (product.customFields.addressLine3HiddenFlag
          ? product.addressLine3
          : ''),
    },
    {
      label: '主要交通',
      value: `${product.customFields.transportMainLine}「${
        product.customFields.transportMainNearest
      }」まで${getTransportMeanLabel(
        product.customFields.transportMainMean
      )}${getTransportTimeLabel(
        product.customFields.transportMainMean,
        product.customFields.transportMainTimeMin,
        product.customFields.transportMainTimeMax,
        product.customFields.transportMainNote
      )}`,
    },
    {
      label: '最寄駅からの時間',
      value: minTime !== null ? `${minTime}分以内` : '-',
    },
    {
      label: '販売戸数',
      value: product.customFields.salesHouseCount
        ? `${product.customFields.salesHouseCount}戸`
        : '未定',
    },
    {
      label: '総戸数',
      value: product.customFields.totalHouseCount
        ? `${product.customFields.totalHouseCount}戸`
        : '未定',
    },
    {
      label: '価格',
      value: formatPriceRange(
        product.customFields.minPrice
          ? Number(product.customFields.minPrice)
          : undefined,
        product.customFields.maxPrice
          ? Number(product.customFields.maxPrice)
          : undefined,
        formatPrice,
        product.customFields.priceRangeType
      ),
    },
    {
      label: '最多価格帯',
      value: (
        <>
          <Typography>
            {product.customFields.mostPriceRanges &&
            product.customFields.mostPriceRanges.length > 0
              ? product.customFields.mostPriceRanges
                  .filter((range) => range !== '')
                  .map((range) => `${range}万円台`)
                  .join('、')
              : '-'}
            <span>
              {product.customFields.mostPriceRangeCount &&
                `(${product.customFields.mostPriceRangeCount}戸)`}
            </span>
          </Typography>
          <Typography>
            {product.customFields.mostPriceRangeAdditional}
          </Typography>
        </>
      ),
    },
    {
      label: '私道負担・道路',
      value: (
        <>
          {product.customFields.minRoadWidth &&
            `道路幅：${product.customFields.minRoadWidth}m`}

          {product.customFields.roadWidthRangeType &&
            product.customFields.maxRoadWidth && (
              <>
                {product.customFields.roadWidthRangeType ===
                ProductRangeType.BETWEEN
                  ? '〜'
                  : product.customFields.roadWidthRangeType ===
                    ProductRangeType.SEPARATE
                  ? '・'
                  : ' '}
              </>
            )}
          {product.customFields.maxRoadWidth &&
            `${product.customFields.maxRoadWidth}m`}
          {(product.customFields.minRoadWidth ||
            product.customFields.maxRoadWidth) &&
            product.customFields.roadPainting &&
            '、'}
          {product.customFields.roadPainting}
          {product.customFields.roadNote &&
            `、${product.customFields.roadNote}`}
        </>
      ),
    },
    {
      label: '諸費用',
      value: (
        <Stack>
          <Typography>{product.customFields.facilityCost}</Typography>
          <Typography>{product.customFields.otherCost}</Typography>
          <Typography>{product.customFields.priceNote}</Typography>
        </Stack>
      ),
    },
    {
      label: '間取りタイプ',
      value: (
        <Stack>
          <Typography>
            {formatFloorPlan(
              product.customFields.floorPlanRooms,
              product.customFields.floorPlanTypeMin,
              product.customFields.floorPlanTypeMax,
              product.customFields.floorPlanRangeType
            )}
          </Typography>
          <Typography color="gray">
            {product.customFields.floorPlanTypeAdditional}
          </Typography>
        </Stack>
      ),
    },
    {
      label: '建物面積',
      value: (
        <Stack>
          {product.customFields.buildingAreaType &&
            Object.values(ProductLandAreaType).find(
              (type) => type.key === product.customFields.buildingAreaType
            )?.label}
          {formatAreaRange(
            product.customFields.minBuildingArea,
            product.customFields.maxBuildingArea,
            product.customFields.buildingAreaRangeType
          )}
          {product.customFields.tsuboNotationFlag && (
            <Typography component="span" variant="body2" sx={{ ml: 1 }}>
              （{convertToTsubo(Number(product.customFields.minBuildingArea))}坪
              {product.customFields.maxBuildingArea
                ? `〜${convertToTsubo(
                    Number(product.customFields.maxBuildingArea)
                  )}坪`
                : ' '}
              ）
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      label: '土地面積',
      value: (
        <Stack>
          {product.customFields.landAreaType &&
            Object.values(ProductLandAreaType).find(
              (type) => type.key === product.customFields.landAreaType
            )?.label}
          {formatAreaRange(
            product.customFields.minLandArea,
            product.customFields.maxLandArea,
            product.customFields.landAreaRangeType
          )}
          {product.customFields.tsuboNotationFlag && (
            <Typography component="span" variant="body2" sx={{ ml: 1 }}>
              （{convertToTsubo(Number(product.customFields.minLandArea))}坪
              {product.customFields.maxLandArea
                ? `〜${convertToTsubo(
                    Number(product.customFields.maxLandArea)
                  )}坪`
                : ' '}
              ）
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      label: '建ペイ率・容積率',
      value: product.customFields.areaRatio,
    },
    {
      label: '完成時期（築年月）',
      value: (
        <>
          {product.customFields.completionDateType &&
            Object.values(ProductCompletionDateType).find(
              (type) => type.key === product.customFields.completionDateType
            )?.label}{' '}
          {product.customFields.completionDateType !==
          ProductCompletionDateType.CONTRACTED.key
            ? format(
                product.customFields?.completionDate
                  ? parseDateStringToDateObj(
                      product.customFields.completionDate
                    )
                  : new Date(),
                'yyyy年M月'
              )
            : `${product.customFields.completionDate}ヶ月`}
          {product.customFields.completionSeason &&
            Object.values(ProductCompletionSeason).find(
              (type) => type.key === product.customFields.completionSeason
            )?.label}
        </>
      ),
    },
    {
      label: '引き渡し可能時期',
      value: (
        <>
          {product.customFields.deliveryDateType &&
            (() => {
              const deliveryType = Object.values(ProductDeliveryDateType).find(
                (type) => type.key === product.customFields.deliveryDateType
              );
              if (
                deliveryType?.key === 'specific' &&
                product.customFields.deliveryDate
              ) {
                const formattedDate = format(
                  parseDateStringToDateObj(product.customFields.deliveryDate),
                  'yyyy年M月'
                );
                const seasonLabel = getDeliverySeasonLabel(
                  product.customFields.deliverySeason
                );
                return `${formattedDate}${seasonLabel}`;
              } else if (deliveryType?.key === 'contracted') {
                return `契約後 ${product.customFields.deliveryDate}ヶ月`;
              }
              return deliveryType?.label || '';
            })()}
        </>
      ),
    },
    {
      label: '土地の権利形態',
      value: (
        <>
          {
            LAND_RIGHT_TYPE_OPTIONS.find(
              (option) => option.value === product.customFields.landRightType
            )?.label
          }
          {product.customFields.landRightType !==
            ProductLandRightType.OWNERSHIP && (
            <>
              、その期間等：
              {product.customFields.landLeaseholdDuration || '-'}
              、その月賃料：
              {product.customFields.landLeaseholdRent || '-'}
              、借地権の場合その他の費用：
              {product.customFields.landLeaseholdCost || '-'}
            </>
          )}
        </>
      ),
    },
    {
      label: '構造・工法',
      value: product.customFields.buildingSystem,
    },
    {
      label: '施工',
      value: product.customFields.constructionCompany,
    },
    {
      label: 'リフォーム',
      value: (
        <Stack>
          <Typography>
            外装：
            {Object.values(ProductReformExterior).find(
              (type) => type.key === product.customFields.reformExterior
            )?.label || '-'}
            ／内装：
            {Object.values(ProductReformInterior).find(
              (type) => type.key === product.customFields.reformInterior
            )?.label || '-'}
          </Typography>
        </Stack>
      ),
    },
    {
      label: '用途地域',
      value: (
        <Stack>
          <Typography>
            {product.customFields.useDistrict &&
              product.customFields.useDistrict
                .filter((district) => district !== '')
                .join('、')}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {product.customFields.buildingPermitReasonType &&
              PROPERTY_BUILDING_PERMIT_REASON_TYPE_VALUES[
                product.customFields.buildingPermitReasonType
              ]}
          </Typography>
        </Stack>
      ),
    },
    {
      label: '地目',
      value: (
        <>
          {product.customFields.landCategory ||
          product.customFields.buildingPermitReason ? (
            <>
              <Typography>{product.customFields.landCategory}</Typography>
              <Typography>
                {product.customFields.buildingPermitReason}
              </Typography>
            </>
          ) : (
            '-'
          )}
        </>
      ),
    },
    {
      label: 'その他概要・特記事項',
      value: (
        <>
          {product.customFields.facility && (
            <Typography>設備:{product.customFields.facility}</Typography>
          )}
          {product.customFields.buildingConfirmationNumber && (
            <Typography>
              建築確認番号:
              {product.customFields.buildingConfirmationNumber}
            </Typography>
          )}
        </>
      ),
    },
  ];
  return (
    <Box>
      <Typography
        width="100%"
        bgcolor={theme.palette.blueAccent.main}
        color="white"
        px={2}
        height={36}
        fontWeight={600}
        borderRadius={15}
        display="flex"
        alignItems="center"
        variant="h6"
      >
        物件情報
      </Typography>
      <Stack spacing={2.5} maxWidth={1040} mx="auto" mt={6} pb={7} width="100%">
        {rows.map(({ label, value }, index) => (
          <Stack
            borderBottom={1}
            borderColor="#E0E0E0"
            key={index}
            direction={{ sm: 'row', xs: 'column' }}
            spacing={1}
            pb={2.5}
          >
            <Typography
              sx={{
                color: '#718CA0',
                fontWeight: 700,
                minWidth: 200,
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </Typography>
            <Box
              sx={{
                whiteSpace: 'pre-wrap',
              }}
            >
              {value}
            </Box>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
